import { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useInstructionStatistics } from "@/services/instructionService";
import { START_YEAR } from "@/types/common";
import PageLayout from "@/components/layout/PageLayout";
import BtnExcelDownload from "@/components/button/BtnExcelDownload";

interface InstructionStatisticsPageProps {
  role: "manager" | "approver";
}

const STAT_KEYS = [
  { key: "pendingReview", name: "등록요청", description: "이행요청 중" },
  { key: "inProgress", name: "이행건", description: "완료요청전 이행" },
  { key: "completionRequested", name: "완료요청", description: "완료요청 중" },
  { key: "closed", name: "종결", description: "종결" },
  { key: "delayed", name: "지연", description: "지연" },
];

function getStatsWithPercentage(data: any) {
  const total = STAT_KEYS.reduce((sum, { key }) => sum + (data?.[key] ?? 0), 0);
  return STAT_KEYS.map(({ key, name, description }) => ({
    name,
    count: data?.[key] ?? 0,
    percentage: total > 0 ? ((data?.[key] ?? 0) / total) * 100 : 0,
    description:
      description + (key === "closed" && data?.delayedClosed ? ` (지연 후 종결: ${data?.delayedClosed})` : ""),
  }));
}

const InstructionStatisticsPage: React.FC<InstructionStatisticsPageProps> = ({ role }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const { data: statistics } = useInstructionStatistics(selectedYear);
  const defaultStats = {
    pendingReview: 0,
    inProgress: 0,
    completionRequested: 0,
    closed: 0,
    delayed: 0,
  };

  // 임시 데이터 - 실제로는 API에서 가져와야 함
  const owner = { ...defaultStats, ...statistics?.owner };
  const entireCompany = { ...defaultStats, ...statistics?.entireCompany };

  const ownerStats = getStatsWithPercentage(owner);
  const companyStats = getStatsWithPercentage(entireCompany);

  const total = {
    name: "총계",
    count: ownerStats.reduce((sum, item) => sum + item.count, 0),
    percentage: 100,
  };

  const ownerClosed = owner.closed ?? 0;
  const ownerDelayedClosed = owner.delayedClosed ?? 0;
  const ownerOnTimeClosed = ownerClosed - ownerDelayedClosed; // 기한일 내 종결
  const ownerTotalCompletedOrDelayed = ownerClosed + (owner.delayed ?? 0); // 종결 + 지연 + 지연후 종결

  const ownerExecutionRate =
    ownerTotalCompletedOrDelayed > 0
      ? Number(((ownerOnTimeClosed / ownerTotalCompletedOrDelayed) * 100).toFixed(2))
      : 0;

  const ownerOnTimeDeliveryRate = ownerClosed > 0 ? Number(((ownerOnTimeClosed / ownerClosed) * 100).toFixed(2)) : 0;
  const ownerChartData = [
    { name: "이행율", rate: ownerExecutionRate }, // 이행율: 종결 / (종결 + 지연 + 지연후 종결)
    { name: "납기준수율", rate: ownerOnTimeDeliveryRate }, // 납기 준수율:  종결 / (종결 + 지연후 종결)
  ];

  // 전사 통계 (approver만)
  const companyClosed = entireCompany.closed ?? 0;
  const companyDelayedClosed = entireCompany.delayedClosed ?? 0;
  const companyOnTimeClosed = companyClosed - companyDelayedClosed; // 기한일 내 종결
  const companyTotalCompletedOrDelayed = companyClosed + (entireCompany.delayed ?? 0); // 종결 + 지연 + 지연후 종결

  const companyExecutionRate =
    companyTotalCompletedOrDelayed > 0
      ? Number(((companyOnTimeClosed / companyTotalCompletedOrDelayed) * 100).toFixed(2))
      : 0;
  const companyOnTimeDeliveryRate =
    companyClosed > 0 ? Number(((companyOnTimeClosed / companyClosed) * 100).toFixed(2)) : 0;

  const companyTotal = {
    name: "전사 총계",
    count: companyStats.reduce((sum, item) => sum + item.count, 0),
    percentage: 100,
  };

  const companyChartData = [
    { name: "전사 이행율", rate: companyExecutionRate },
    { name: "전사 납기준수율", rate: companyOnTimeDeliveryRate },
  ];

  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = START_YEAR; y <= thisYear; y++) {
    years.push(y);
  }

  return (
    <PageLayout>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, minHeight: 0 }}>
        <Typography variant='h5' component='h1'>
          지시사항 통계
        </Typography>
        <FormControl size='small' sx={{ minWidth: 120 }}>
          <InputLabel>연도</InputLabel>
          <Select value={selectedYear} label='연도' onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}년
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", gap: 3, minHeight: 0, flex: role === "approver" ? 1 : undefined }}>
        {/* 통계 테이블 */}
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: "1px solid #ddd",
            borderRadius: 2,
            boxShadow: "none",
            overflow: "hidden",
            p: 0,
          }}
          elevation={0}
        >
          <TableContainer sx={{ p: 0, m: 0, flex: 1, minHeight: 0, height: "100%" }}>
            <Table sx={{ height: "100%" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>항목</TableCell>
                  <TableCell align='right'>건수</TableCell>
                  <TableCell align='right'>비율 (%)</TableCell>
                  <TableCell>비고</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ownerStats.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align='right'>{row.count}</TableCell>
                    <TableCell align='right'>{row.percentage.toFixed(2)}%</TableCell>
                    <TableCell>{row.description}</TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{
                    "& td, & th": { borderBottom: "none" },
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold" }}>{total.name}</TableCell>
                  <TableCell align='right' sx={{ fontWeight: "bold" }}>
                    {total.count}
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: "bold" }}>
                    {total.percentage.toFixed(2)}%
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 차트 */}
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: "1px solid #ddd",
            borderRadius: 2,
            boxShadow: "none",
            overflow: "hidden",
            p: 2,
          }}
        >
          <Typography variant='h6' sx={{ mb: 2 }}>
            이행율 및 납기준수율
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={ownerChartData}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey='rate' fill='#1976d2' name='비율' />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* approver만 전사 통계 */}
      {role === "approver" && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, minHeight: 0 }}>
            <Typography variant='h5' component='h1'>
              전사 통계
            </Typography>
            <BtnExcelDownload
              apiUrl={`/approver/instructions/statistics/${selectedYear}/excel-export`}
              title='전 부서 통계 엑셀 다운로드'
            />
          </Box>
          <Box sx={{ display: "flex", flex: 1, gap: 3, minHeight: 0 }}>
            <Paper
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                border: "1px solid #ddd",
                borderRadius: 2,
                boxShadow: "none",
                overflow: "hidden",
                p: 0,
              }}
              elevation={0}
            >
              <TableContainer sx={{ p: 0, m: 0, flex: 1, minHeight: 0, height: "100%" }}>
                <Table sx={{ height: "100%" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell>항목</TableCell>
                      <TableCell align='right'>건수</TableCell>
                      <TableCell align='right'>비율 (%)</TableCell>
                      <TableCell>비고</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companyStats.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell align='right'>{row.count}</TableCell>
                        <TableCell align='right'>{row.percentage.toFixed(2)}%</TableCell>
                        <TableCell>{row.description}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ "& td, & th": { borderBottom: "none" } }}>
                      <TableCell sx={{ fontWeight: "bold" }}>{companyTotal.name}</TableCell>
                      <TableCell align='right' sx={{ fontWeight: "bold" }}>
                        {companyTotal.count}
                      </TableCell>
                      <TableCell align='right' sx={{ fontWeight: "bold" }}>
                        {companyTotal.percentage.toFixed(2)}%
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                border: "1px solid #ddd",
                borderRadius: 2,
                boxShadow: "none",
                overflow: "hidden",
                p: 2,
              }}
            >
              <Typography variant='h6' sx={{ mb: 2 }}>
                전사 이행율 및 납기준수율
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={companyChartData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey='rate' fill='#1976d2' name='비율' />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        </>
      )}
    </PageLayout>
  );
};

export default InstructionStatisticsPage;
